import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, uploadFile, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canManage, isCleaner } from "@/lib/perms";
import { CONDITION_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ImagePlus, Loader2, Search, BedDouble, Bath, AlertTriangle, CalendarX } from "lucide-react";
import { DirectionsButton } from "@/components/DirectionsButton";
import { CalendarSyncBadge } from "@/components/CalendarSyncBadge";

const EMPTY = {
  name: "", address: "", city: "", state: "", bedrooms: 1, bathrooms: 1,
  owner_id: "", access_notes: "", wifi: "", ical_url: "", notes: "", photos: [], condition: "good",
};

/** Say what actually went wrong. "Something went wrong" is what the user saw for every failure,
 *  including the ones we can name: a plan limit, a session that expired, or a request that never
 *  reached the API. */
function saveErrorMessage(err) {
  const status = err.response?.status;
  const detail = err.response?.data?.detail;
  if (detail) return formatApiErrorDetail(detail);
  if (!err.response) return "No connection to the server — check your signal and try again.";
  if (status === 401) return "Your session expired. Sign in again to save.";
  if (status === 413) return "Those photos are too large. Remove one and try again.";
  if (status >= 500) return "The server could not save that. Try again in a moment.";
  return "Could not save";
}

export function PropertyForm({ initial, owners, onSaved, onClose }) {
  // A saved record carries nulls (ical_url, owner_id, notes); feeding null to an input makes it
  // uncontrolled and React stops tracking what the user types into it.
  const [form, setForm] = useState(() => {
    const merged = { ...EMPTY, ...(initial || {}) };
    Object.keys(EMPTY).forEach((k) => {
      if (merged[k] === null || merged[k] === undefined) merged[k] = EMPTY[k];
    });
    return merged;
  });  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) paths.push(await uploadFile(f));
      set("photos", [...(form.photos || []), ...paths]);
      toast.success(`${paths.length} photo(s) uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
    setUploading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    // A cleared number field reads as "" — and Number("") is 0, so a blank box would quietly
    // save "0 bedrooms". Blank means "leave it as it was".
    const num = (value, fallback) => {
      if (value === "" || value === null || value === undefined) return fallback;
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    };
    const payload = {
      ...form,
      bedrooms: num(form.bedrooms, initial?.bedrooms ?? 1),
      bathrooms: num(form.bathrooms, initial?.bathrooms ?? 1),
      owner_id: form.owner_id || null,
      ical_url: form.ical_url || null,
    };
    ["_id", "owner_name", "owner_email", "open_issues", "created_at", "tenant_id",
     "ical_sync_error", "ical_synced_at", "condition_updated_at"].forEach((k) => delete payload[k]);
    try {
      const { data } = initial?._id
        ? await api.put(`/properties/${initial._id}`, payload)
        : await api.post("/properties", payload);
      toast.success(initial?._id ? "Property updated" : "Property added");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(saveErrorMessage(err));
    }
    setBusy(false);
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="property-form">
      <section data-testid="form-section-property-details" className="space-y-4">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-base font-semibold text-royal-deep">Property Details</h3>
          <div className="h-px flex-1 gold-rule" />
        </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Property name</Label>
          <Input data-testid="property-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Address</Label>
          <Input data-testid="property-address-input" required value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">City</Label>
          <Input data-testid="property-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">State</Label>
          <Input data-testid="property-state-input" value={form.state} onChange={(e) => set("state", e.target.value)} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bedrooms</Label>
          <Input data-testid="property-bedrooms-input" type="number" min="0" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bathrooms</Label>
          <Input data-testid="property-bathrooms-input" type="number" min="0" step="0.5" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Owner</Label>
          <Select value={form.owner_id || "none"} onValueChange={(v) => set("owner_id", v === "none" ? "" : v)}>
            <SelectTrigger data-testid="property-owner-select" className="mt-1.5"><SelectValue placeholder="Select owner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No owner assigned</SelectItem>
              {owners.map((o) => (
                <SelectItem key={o._id} value={o._id} data-testid={`owner-option-${o._id}`}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Condition</Label>
          <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
            <SelectTrigger data-testid="property-condition-select" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(CONDITION_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      </section>

      <section data-testid="form-section-access" className="space-y-4">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-base font-semibold text-royal-deep">Access & Utilities</h3>
          <div className="h-px flex-1 gold-rule" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Access notes / lockbox</Label>
          <Input data-testid="property-access-input" value={form.access_notes} onChange={(e) => set("access_notes", e.target.value)} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">WiFi</Label>
          <Input data-testid="property-wifi-input" value={form.wifi} onChange={(e) => set("wifi", e.target.value)} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            Booking calendar link (Airbnb / VRBO iCal)
          </Label>
          <Input data-testid="property-ical-input" value={form.ical_url}
            onChange={(e) => set("ical_url", e.target.value)}
            placeholder="https://www.airbnb.com/calendar/ical/…"
            className="mt-1.5" />
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            Paste the export link from your listing and Cornerstone Ops will schedule a turnover after
            every guest checkout, every night.
          </p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Care notes</Label>
          <Textarea data-testid="property-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5" rows={3} />
        </div>
      </section>

      <div>
        <div className="flex items-center gap-3 mb-3">
          <h3 className="font-display text-base font-semibold text-royal-deep">Photos</h3>
          <div className="h-px flex-1 gold-rule" />
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {(form.photos || []).map((p, i) => (
            <div key={p} className="relative h-20 w-24 rounded-md overflow-hidden border border-border">
              <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                data-testid={`remove-photo-${i}`}
                onClick={() => set("photos", form.photos.filter((_, x) => x !== i))}
                className="absolute top-0 right-0 bg-black/60 text-white text-[10px] px-1.5"
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            data-testid="upload-photo-btn"
            onClick={() => fileRef.current?.click()}
            className="h-20 w-24 rounded-md border border-dashed border-royal/30 flex flex-col items-center justify-center gap-1 text-royal hover:bg-royal/5 transition-colors duration-200"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            <span className="text-[10px]">Add photos</span>
          </button>
          <input ref={fileRef} data-testid="property-photo-input" type="file" accept="image/*" multiple hidden onChange={pickPhotos} />
        </div>
      </div>

      <Button data-testid="save-property-btn" type="submit" disabled={busy} className="w-full bg-royal hover:bg-royal-deep text-white">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : initial?._id ? "Save changes" : "Add property"}
      </Button>
    </form>
  );
}

export default function Properties() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(() => api.get("/properties").then(({ data }) => setProperties(data)), []);

  useEffect(() => {
    load();
    if (canManage(user)) api.get("/users", { params: { role: "owner" } }).then(({ data }) => setOwners(data));
  }, [user, load]);

  const filtered = properties.filter(
    (p) => p.name.toLowerCase().includes(q.toLowerCase()) || p.address.toLowerCase().includes(q.toLowerCase())
  );
  const failedSyncs = properties.filter((p) => p.ical_url && p.ical_sync_error).length;

  return (
    <div className="space-y-6" data-testid="properties-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Portfolio</p>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">Properties</h1>
          {failedSyncs > 0 && (
            <p data-testid="ical-failed-count"
               className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-red-200
                          bg-red-50 px-2.5 py-1 text-[11px] font-medium text-red-700">
              <CalendarX className="h-3 w-3" />
              {failedSyncs} booking {failedSyncs === 1 ? "calendar" : "calendars"} failed to sync
            </p>
          )}
        </div>
        {canManage(user) && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-property-btn" className="bg-royal hover:bg-royal-deep text-white gap-2">
                <Plus className="h-4 w-4" /> Add property
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-royal-deep">New property</DialogTitle></DialogHeader>
              <PropertyForm owners={owners} onSaved={load} onClose={() => setOpen(false)} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="property-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search properties" className="pl-9" />
      </div>

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="properties-grid">
        {filtered.length === 0 && <p className="text-sm text-muted-foreground">No properties found.</p>}
        {filtered.map((p, idx) => (
          <Link
            key={p._id}
            to={`/properties/${p._id}`}
            data-testid={`property-card-${p._id}`}
            style={{ animationDelay: `${idx * 60}ms` }}
            className="rise rounded-lg border border-border bg-white overflow-hidden hover:shadow-md hover:-translate-y-[2px] transition-transform duration-200"
          >
            <div className="h-44 bg-secondary overflow-hidden relative">
              {p.photos?.[0] ? (
                <img src={fileUrl(p.photos[0])} alt={p.name} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-xs text-muted-foreground">No photo yet</div>
              )}
              <div className="absolute top-2.5 right-2.5 z-10">
                <DirectionsButton property={p} size="sm" />
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display text-lg font-semibold text-royal-deep leading-tight">{p.name}</h3>
                {p.open_issues > 0 && (
                  <span className="flex items-center gap-1 text-[11px] rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-red-700">
                    <AlertTriangle className="h-3 w-3" /> {p.open_issues}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {p.address}{p.city ? `, ${p.city}` : ""} {p.state}
              </p>
              <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><BedDouble className="h-3.5 w-3.5 text-royal" /> {p.bedrooms}</span>
                <span className="flex items-center gap-1.5"><Bath className="h-3.5 w-3.5 text-royal" /> {p.bathrooms}</span>
                <span className="ml-auto rounded-full bg-gold/15 border border-gold/30 px-2 py-0.5 text-royal-deep font-medium">
                  {CONDITION_LABELS[p.condition] || p.condition}
                </span>
              </div>
              {p.ical_url && (
                <div className="mt-3"><CalendarSyncBadge property={p} /></div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
